import {
    ArrowForward,
    Close as CloseIcon,
    KeyboardArrowDown,
    Menu as MenuIcon,
    Notifications,
    Search as SearchIcon
} from '@mui/icons-material';
import {
    AppBar,
    Badge,
    Box,
    Button,
    Card,
    CardContent,
    CardMedia,
    Chip,
    ClickAwayListener,
    Divider,
    Drawer,
    Grid,
    IconButton,
    List,
    ListItem,
    ListItemText,
    Paper,
    Popper,
    Portal,
    Toolbar,
    Typography,
    useMediaQuery,
    useTheme
} from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import { ApplicationLogo, LanguagesMenu, UserAvatar } from '../../../../Components';
import MicrospecThemeModePicker from '../../../../Components/MicrospecThemeModePicker';
import { getOrDefault } from '../../../../Helpers/Utils';
import { useMicrospecThemeLanguageContext } from '../../../../Themes';
import { ActionItem, Category, CategoryItem, MegaMenuContentProps, MegaMenuNavbarProps, MenuItem, QuickAction } from './types';

const MegaMenuNavbar: React.FC<MegaMenuNavbarProps> = ({ spec, type, index }) => {
    const theme = useTheme();
    const { localizationProvider } = useMicrospecThemeLanguageContext();
    const isMobile = useMediaQuery(theme.breakpoints.down('lg'));
    const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isExtraSmall = useMediaQuery('(max-width:375px)');

    // State management
    const [mobileMenuOpen, setMobileMenuOpen] = useState<boolean>(false);
    const [activeMegaMenu, setActiveMegaMenu] = useState<string | null>(null);
    const [megaMenuAnchor, setMegaMenuAnchor] = useState<HTMLElement | null>(null);
    const megaMenuTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const navbarRef = useRef<HTMLDivElement | null>(null); // Reference to the navbar
    
    // Scroll state management
    const [isVisible, setIsVisible] = useState<boolean>(true);
    const [lastScrollY, setLastScrollY] = useState<number>(0);
    const scrollThreshold = 10; // Minimum scroll distance to trigger hide/show
    const backgroundColor = getOrDefault(
        spec.backgroundColor,
        theme.palette.mode === 'dark'
            ? 'rgba(18, 18, 18, 0.95)'
            : 'rgba(255, 255, 255, 0.95)'
    );
    const textColor = getOrDefault(spec.textColor, theme.palette.text.primary);
    const logoText = getOrDefault(spec.logoText, localizationProvider.translateByType({
        type: "Localized",
        text: "application.name"
    }));
    const height = getOrDefault(spec.height, '80px');
    const transparent = getOrDefault(spec.transparent, false);
    const blur = getOrDefault(spec.blur, true);
    const shadow = getOrDefault(spec.shadow, true);
    const sticky = getOrDefault(spec.sticky, true);
    const menuItems = getOrDefault(spec.menuItems, []);
    const megaMenus = getOrDefault(spec.megaMenus, {});
    const actions = getOrDefault(spec.actions, []);
    const showLanguagesMenu = getOrDefault(spec.showLanguagesMenu, false);
    const showThemeModeToggler = getOrDefault(spec.showThemeModeToggler, false);
    const showSearch = getOrDefault(spec.showSearch, false);
    const showNotifications = getOrDefault(spec.showNotifications, false);
    const showProfile = getOrDefault(spec.showProfile, true);
    const brandColor = getOrDefault(
        spec.brandColor,
        theme.palette.mode === 'dark'
            ? "#f0f0f0"
            : theme.palette.primary.main
    );
    const secondaryColor = getOrDefault(spec.secondaryColor, theme.palette.secondary.main);
    const megaMenuWidth = getOrDefault(spec.megaMenuWidth, '1200px');

    // Scroll detection effect
    useEffect(() => {
        let ticking = false;

        const handleScroll = () => {
            const currentScrollY = window.scrollY;

            if (!ticking) {
                window.requestAnimationFrame(() => {
                    // Show navbar when at top or scrolling up
                    if (currentScrollY < scrollThreshold) {
                        setIsVisible(true);
                    } else if (currentScrollY < lastScrollY) {
                        // Scrolling up
                        setIsVisible(true);
                    } else if (currentScrollY > lastScrollY && currentScrollY > scrollThreshold) {
                        // Scrolling down
                        setIsVisible(false);
                    }

                    setLastScrollY(currentScrollY);
                    ticking = false;
                });

                ticking = true;
            }
        };

        window.addEventListener('scroll', handleScroll, { passive: true });

        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, [lastScrollY, scrollThreshold]);

    // Create dynamic styles
    const navbarStyles = {
        background: transparent
            ? 'transparent'
            : backgroundColor,
        backdropFilter: blur ? 'blur(15px)' : 'none',
        WebkitBackdropFilter: blur ? 'blur(15px)' : 'none',
        boxShadow: shadow
            ? theme.palette.mode === 'dark'
                ? '0 4px 30px rgba(0, 0, 0, 0.5)'
                : '0 4px 30px rgba(0, 0, 0, 0.1)'
            : 'none',
        border: transparent
            ? 'none'
            : theme.palette.mode === 'dark'
                ? '1px solid rgba(255, 255, 255, 0.1)'
                : '1px solid rgba(0, 0, 0, 0.1)',
        // position and top are handled by AppBar's position prop directly
        zIndex: 1200, // Reduced to allow mega menu to appear above
        height,
        transform: isVisible ? 'translateY(0)' : 'translateY(-100%)',
        transition: 'all 0.3s ease',
        '&:hover': {
            boxShadow: shadow
                ? theme.palette.mode === 'dark'
                    ? '0 8px 40px rgba(0, 0, 0, 0.7)'
                    : '0 8px 40px rgba(0, 0, 0, 0.15)'
                : 'none'
        },
    };

    // Mega menu handlers
    const handleMegaMenuEnter = (event: React.MouseEvent<HTMLElement>, menuKey: string) => {
        if (megaMenuTimeoutRef.current) {
            clearTimeout(megaMenuTimeoutRef.current);
        }
        // Use navbar as anchor instead of the specific menu item
        setMegaMenuAnchor(navbarRef.current);
        setActiveMegaMenu(menuKey);
    };

    const handleMegaMenuLeave = () => {
        megaMenuTimeoutRef.current = setTimeout(() => {
            setActiveMegaMenu(null);
            setMegaMenuAnchor(null);
        }, 150);
    };

    const handleMegaMenuPaperEnter = () => {
        if (megaMenuTimeoutRef.current) {
            clearTimeout(megaMenuTimeoutRef.current);
        }
    };

    const handleMegaMenuPaperLeave = () => {
        setActiveMegaMenu(null);
        setMegaMenuAnchor(null);
    };

    const handleMobileMenuToggle = () => {
        setMobileMenuOpen(!mobileMenuOpen);
    };

    // Mega Menu Content Component
    const MegaMenuContent: React.FC<MegaMenuContentProps> = ({ menuData }) => {
        if (!menuData) return null;

        return (
            <Box sx={{
                maxWidth: megaMenuWidth,
                mx: 'auto', // Center the content
                p: 3,
            }}>
                <Grid container spacing={3}>
                    {/* Featured Section */}
                    {menuData.featured && (
                        <Grid item xs={12} md={4}>
                            <Card
                                elevation={0}
                                sx={{
                                    background: `linear-gradient(135deg, ${brandColor}15 0%, ${secondaryColor}15 100%)`,
                                    border: `1px solid ${brandColor}20`,
                                    borderRadius: 3,
                                    overflow: 'hidden',
                                    height: '100%'
                                }}
                            >
                                {menuData.featured.image && (
                                    <CardMedia
                                        component="img"
                                        height="140"
                                        image={menuData.featured.image}
                                        alt={menuData.featured.title}
                                        title={menuData.featured.title}
                                    />
                                )}
                                <CardContent>
                                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, color: theme.palette.text.primary }}>
                                        {menuData.featured.title}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                        {menuData.featured.description}
                                    </Typography>
                                    <Button
                                        size="small"
                                        endIcon={<ArrowForward />}
                                        sx={{
                                            color: brandColor,
                                            fontWeight: 600,
                                            textTransform: 'none'
                                        }}
                                    >
                                        {menuData.featured.buttonText || 'اكتشف المزيد'}
                                    </Button>
                                </CardContent>
                            </Card>
                        </Grid>
                    )}

                    {/* Categories Sections */}
                    {menuData.categories && menuData.categories.map((category: Category, idx: number) => (
                        <Grid item xs={12} md={menuData.featured ? 4 : 6} lg={menuData.featured ? 2 : 3} key={idx}>
                            <Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                    {category.icon && (
                                        <Box
                                            sx={{
                                                width: 24,
                                                height: 24,
                                                borderRadius: 1,
                                                background: `${brandColor}20`,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                mr: 1,
                                                color: brandColor
                                            }}
                                        >
                                            {category.icon}
                                        </Box>
                                    )}
                                    <Typography
                                        variant="subtitle1"
                                        sx={{
                                            fontWeight: 700,
                                            color: brandColor,
                                            fontSize: '1rem'
                                        }}
                                    >
                                        {category.title}
                                    </Typography>
                                </Box>
                                <List dense sx={{ py: 0 }}>
                                    {category.items.map((item: CategoryItem, itemIdx: number) => (
                                        <ListItem
                                            key={itemIdx}
                                            button
                                            component="a"
                                            href={item.href}
                                            sx={{
                                                py: 0.5,
                                                px: 0,
                                                borderRadius: 1,
                                                transition: 'all 0.2s ease',
                                                '&:hover': {
                                                    backgroundColor: `${brandColor}10`,
                                                    transform: 'translateX(5px)',
                                                    color: brandColor
                                                }
                                            }}
                                        >
                                            <ListItemText
                                                primary={
                                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                                        <Typography variant="body2" sx={{ fontWeight: 500, color: theme.palette.text.primary }}>
                                                            {item.label}
                                                        </Typography>
                                                        {item.badge && (
                                                            <Chip
                                                                label={item.badge}
                                                                size="small"
                                                                sx={{
                                                                    height: 18,
                                                                    fontSize: '0.6rem',
                                                                    background: item.badge === 'جديد'
                                                                        ? theme.palette.error.main
                                                                        : secondaryColor,
                                                                    color: 'white'
                                                                }}
                                                            />
                                                        )}
                                                    </Box>
                                                }
                                            />
                                        </ListItem>
                                    ))}
                                </List>
                            </Box>
                        </Grid>
                    ))}
                </Grid>

                {/* Quick Actions */}
                {menuData.quickActions && (
                    <>
                        <Divider sx={{ my: 2, borderColor: `${brandColor}20` }} />
                        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                            {menuData.quickActions.map((action: QuickAction, idx: number) => (
                                <Button
                                    key={idx}
                                    size="small"
                                    startIcon={action.icon}
                                    href={action.href}
                                    sx={{
                                        borderRadius: 2,
                                        textTransform: 'none',
                                        fontWeight: 500,
                                        color: theme.palette.text.primary,
                                        border: `1px solid ${brandColor}30`,
                                        '&:hover': {
                                            background: `${brandColor}10`,
                                            borderColor: brandColor,
                                            transform: 'translateY(-1px)'
                                        }
                                    }}
                                >
                                    {action.label}
                                </Button>
                            ))}
                        </Box>
                    </>
                )}
            </Box>
        );
    };

    // Desktop menu items
    const DesktopMenuItems: React.FC = () => (
        <Box sx={{ display: { xs: 'none', lg: 'flex' }, alignItems: 'center', gap: 1 }}>
            {menuItems.map((item: MenuItem, idx: number) => {
                const hasMegaMenu = megaMenus[item.megaMenuKey || ''];

                return (
                    <Button
                        key={idx}
                        href={!hasMegaMenu ? item.href : undefined}
                        onMouseEnter={hasMegaMenu ? (e) => handleMegaMenuEnter(e, item.megaMenuKey!) : undefined}
                        onMouseLeave={hasMegaMenu ? handleMegaMenuLeave : undefined}
                        sx={{
                            color: textColor,
                            textTransform: 'none',
                            fontWeight: 600,
                            fontSize: '1rem',
                            px: 3,
                            py: 1.5,
                            borderRadius: 3,
                            position: 'relative',
                            transition: 'all 0.3s ease',
                            '&:hover': {
                                color: brandColor,
                                backgroundColor: `${brandColor}10`,
                                transform: 'translateY(-2px)',
                                boxShadow: `0 4px 20px ${brandColor}30`
                            },
                            '&::after': {
                                content: '""',
                                position: 'absolute',
                                bottom: 0,
                                left: '50%',
                                width: 0,
                                height: '3px',
                                background: `linear-gradient(90deg, ${brandColor} 0%, ${secondaryColor} 100%)`,
                                transition: 'all 0.3s ease',
                                transform: 'translateX(-50%)',
                                borderRadius: '2px 2px 0 0'
                            },
                            '&:hover::after': {
                                width: '80%'
                            }
                        }}
                        endIcon={hasMegaMenu ? <KeyboardArrowDown /> : item.hasSubmenu ? <KeyboardArrowDown /> : null}
                    >
                        {item.label}
                        {item.badge && (
                            <Chip
                                label={item.badge}
                                size="small"
                                sx={{
                                    ml: 1,
                                    height: 20,
                                    fontSize: '0.7rem',
                                    background: item.badge === 'جديد'
                                        ? theme.palette.error.main
                                        : secondaryColor,
                                    color: 'white',
                                    fontWeight: 600
                                }}
                            />
                        )}
                    </Button>
                );
            })}
        </Box>
    );

    // Action buttons
    const ActionButtons: React.FC = () => (
        <Box sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: isSmallMobile ? 0.5 : 1
        }}>
            {
                showLanguagesMenu && !isSmallMobile && (
                    <LanguagesMenu />
                )
            }
            {
                showThemeModeToggler && !isSmallMobile && (
                    <MicrospecThemeModePicker />
                )
            }
            {!isSmallMobile && actions.map((action: ActionItem, actionIndex: number) => (
                <IconButton key={actionIndex} sx={{
                    color: theme.palette.mode === 'dark' ? theme.palette.primary.light : theme.palette.primary.main,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                        color: theme.palette.mode === 'dark' ? theme.palette.primary.light : theme.palette.primary.main,
                        backgroundColor: `${theme.palette.action.hover}15`,
                        transform: 'scale(1.1)',
                        boxShadow: `0 4px 15px ${brandColor}30`
                    }
                }}>
                    {action.icon}
                </IconButton>
            ))}
            {showSearch && !isSmallMobile && (
                <IconButton
                    sx={{
                        color: textColor,
                        width: 44,
                        height: 44,
                        transition: 'all 0.3s ease',
                        '&:hover': {
                            color: brandColor,
                            backgroundColor: `${brandColor}15`,
                            transform: 'scale(1.1)',
                            boxShadow: `0 4px 15px ${brandColor}30`
                        }
                    }}
                >
                    <SearchIcon fontSize="medium" />
                </IconButton>
            )}

            {showNotifications && !isSmallMobile && (
                <IconButton
                    sx={{
                        color: textColor,
                        width: 44,
                        height: 44,
                        position: 'relative',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                            color: brandColor,
                            backgroundColor: `${brandColor}15`,
                            transform: 'scale(1.1)',
                            boxShadow: `0 4px 15px ${brandColor}30`
                        }
                    }}
                >
                    <Badge badgeContent={3} color="error" sx={{ '& .MuiBadge-badge': { fontSize: '0.75rem' } }}>
                        <Notifications fontSize="medium" />
                    </Badge>
                </IconButton>
            )}

            {showProfile && (
                <UserAvatar
                    size="medium"
                />
            )}
        </Box>
    );

    // Mobile drawer
    const MobileDrawer: React.FC = () => (
        <Drawer
            anchor="right"
            open={mobileMenuOpen}
            onClose={handleMobileMenuToggle}
            PaperProps={{
                sx: {
                    width: isExtraSmall ? '100vw' : isSmallMobile ? 280 : 320,
                    maxWidth: '100vw',
                    background: `linear-gradient(135deg, ${backgroundColor} 0%, ${secondaryColor}10 100%)`,
                    backdropFilter: 'blur(20px)'
                }
            }}
        >
            <Box sx={{
                p: isSmallMobile ? 2 : 3,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                borderBottom: `1px solid ${brandColor}20`
            }}>
                <Box sx={{
                    display: 'flex',
                    alignItems: "center"
                }}>
                    <ApplicationLogo
                        height={"70%"}
                        sx={{ mx: 0.5 }}
                        enableHoveringEffect={false}
                        animate={false}
                        overrideDefaultLogo={false}
                    />
                    <Typography
                        variant="h6"
                        sx={{
                            fontWeight: 700,
                            color: brandColor,
                            fontSize: '1.1rem'
                        }}
                    >
                        {logoText}
                    </Typography>
                </Box>
                <IconButton
                    onClick={handleMobileMenuToggle}
                    sx={{
                        color: theme.palette.text.primary,
                        width: 36,
                        height: 36
                    }}
                >
                    <CloseIcon fontSize="small" />
                </IconButton>
            </Box>

            {/* Mobile Action Buttons - Only shown on small mobile */}
            {isSmallMobile && (showLanguagesMenu || showThemeModeToggler || actions.length > 0 || showSearch || showNotifications) && (
                <Box sx={{
                    p: 2,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                    borderBottom: `1px solid ${brandColor}10`
                }}>
                    {/* Languages and Theme Mode */}
                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-start', alignItems: 'center' }}>
                        {showLanguagesMenu && <LanguagesMenu />}
                        {showThemeModeToggler && <MicrospecThemeModePicker />}
                    </Box>

                    {/* Custom Actions */}
                    {actions.length > 0 && (
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                            {actions.map((action: ActionItem, actionIndex: number) => (
                                <IconButton
                                    key={actionIndex}
                                    sx={{
                                        color: theme.palette.mode === 'dark' ? theme.palette.primary.light : theme.palette.primary.main,
                                        border: `1px solid ${brandColor}30`,
                                        '&:hover': {
                                            backgroundColor: `${brandColor}10`,
                                            borderColor: brandColor
                                        }
                                    }}
                                >
                                    {action.icon}
                                </IconButton>
                            ))}
                        </Box>
                    )}

                    {/* Search and Notifications */}
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        {showSearch && (
                            <Button
                                fullWidth
                                startIcon={<SearchIcon />}
                                variant="outlined"
                                sx={{
                                    borderColor: `${brandColor}30`,
                                    color: theme.palette.text.primary,
                                    textTransform: 'none',
                                    '&:hover': {
                                        borderColor: brandColor,
                                        backgroundColor: `${brandColor}10`
                                    }
                                }}
                            >
                                البحث
                            </Button>
                        )}
                        {showNotifications && (
                            <IconButton
                                sx={{
                                    color: theme.palette.text.primary,
                                    border: `1px solid ${brandColor}30`,
                                    '&:hover': {
                                        backgroundColor: `${brandColor}10`,
                                        borderColor: brandColor
                                    }
                                }}
                            >
                                <Badge badgeContent={3} color="error">
                                    <Notifications />
                                </Badge>
                            </IconButton>
                        )}
                    </Box>
                </Box>
            )}

            <List sx={{ px: 1 }}>
                {menuItems.map((item: MenuItem, idx: number) => (
                    <ListItem
                        key={idx}
                        button
                        component="a"
                        href={item.href}
                        sx={{
                            mx: 1,
                            mb: 1,
                            borderRadius: 3,
                            transition: 'all 0.3s ease',
                            '&:hover': {
                                backgroundColor: `${brandColor}20`,
                                transform: 'translateX(10px)'
                            }
                        }}
                    >
                        <ListItemText
                            primary={
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                    <Typography sx={{ fontWeight: 600, color: theme.palette.text.primary, fontSize: '1rem' }}>
                                        {item.label}
                                    </Typography>
                                    {item.badge && (
                                        <Chip
                                            label={item.badge}
                                            size="small"
                                            sx={{
                                                height: 20,
                                                fontSize: '0.7rem',
                                                background: item.badge === 'جديد'
                                                    ? theme.palette.error.main
                                                    : secondaryColor,
                                                color: 'white'
                                            }}
                                        />
                                    )}
                                </Box>
                            }
                        />
                    </ListItem>
                ))}
            </List>
        </Drawer>
    );

    return (
        <Box id={spec.id} key={`${type}-${index}-box`}>
            <AppBar
                ref={navbarRef}
                position={sticky ? 'fixed' : 'static'}
                elevation={0}
                sx={navbarStyles}
            >
                <Toolbar
                    sx={{
                        minHeight: height,
                        px: { xs: 1, sm: 2, md: 4, lg: 6 },
                        justifyContent: 'space-between',
                        gap: { xs: 0.5, sm: 1 }
                    }}
                >
                    <Box sx={{
                        minWidth: "60%",
                        height: '100%',
                        display: 'flex',
                        alignItems: 'center'
                    }}>
                        <ApplicationLogo
                            height={"60%"}
                            width={isSmallMobile ? "90px" : "120px"}
                            sx={{ mx: 1 }}
                            enableHoveringEffect={false}
                            animate={false}
                            overrideDefaultLogo={false}
                        />
                        <Typography
                            variant="h6"
                            sx={{
                                fontWeight: 700,
                                color: brandColor,
                                fontSize: '1.1rem'
                            }}
                        >
                            {logoText}
                        </Typography>
                    </Box>
                    <DesktopMenuItems />

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <ActionButtons />

                        {/* Mobile menu button */}
                        {<IconButton
                            sx={{
                                display: { xs: 'flex', lg: 'none' },
                                color: textColor,
                                width: isSmallMobile ? 36 : 44,
                                height: isSmallMobile ? 36 : 44,
                                transition: 'all 0.3s ease',
                                '&:hover': {
                                    color: brandColor,
                                    backgroundColor: `${brandColor}15`,
                                    transform: isSmallMobile ? 'none' : 'rotate(90deg)'
                                }
                            }}
                            onClick={handleMobileMenuToggle}
                        >
                            <MenuIcon fontSize={isSmallMobile ? 'small' : 'medium'} />
                        </IconButton>}
                    </Box>
                </Toolbar>
            </AppBar>

            {/* Spacer to prevent content overlap when sticky */}
            {sticky && <Box sx={{ height }} />}

            {/* Mega Menu Popper - Only show on desktop */}
            {!isMobile && (
                <Portal>
                    <Popper
                        open={Boolean(activeMegaMenu)}
                        anchorEl={megaMenuAnchor}
                        placement="bottom"
                        sx={{ zIndex: 1400 }} // Much higher z-index
                        modifiers={[
                            {
                                name: 'offset',
                                options: {
                                    offset: [0, 0], // No gap, start right under navbar
                                },
                            },
                            {
                                name: 'preventOverflow',
                                options: {
                                    boundary: 'viewport',
                                    altBoundary: true,
                                    padding: 8,
                                },
                            },
                        ]}
                    >
                        <ClickAwayListener onClickAway={handleMegaMenuPaperLeave}>
                            <Paper
                                onMouseEnter={handleMegaMenuPaperEnter}
                                onMouseLeave={handleMegaMenuPaperLeave}
                                elevation={24}
                                sx={{
                                    width: '100vw', // Full viewport width
                                    borderRadius: 0, // No border radius for seamless connection
                                    overflow: 'hidden',
                                    background: theme.palette.mode === 'dark'
                                        ? 'rgba(18, 18, 18, 0.98)'
                                        : 'rgba(255, 255, 255, 0.98)',
                                    backdropFilter: 'blur(20px)',
                                    border: 'none', // Remove border for seamless look
                                    borderTop: `1px solid ${brandColor}10`, // Subtle top border
                                    boxShadow: `0 10px 40px ${theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.5)' : 'rgba(0, 0, 0, 0.1)'}`,
                                    animation: 'slideDown 0.3s ease',
                                    position: 'relative',
                                    zIndex: 1401, // Highest z-index
                                    marginLeft: '-50vw', // Center the full-width container
                                    left: '50%'
                                }}
                            >
                                <MegaMenuContent menuData={megaMenus[activeMegaMenu || '']} />
                            </Paper>
                        </ClickAwayListener>
                    </Popper>
                </Portal>
            )}

            {/* Mobile Drawer */}
            <MobileDrawer />

            {/* Add animations */}
            <style>
                {`
                    @keyframes slideDown {
                        from {
                            opacity: 0;
                            transform: translateY(-10px);
                        }
                        to {
                            opacity: 1;
                            transform: translateY(0);
                        }
                    }
                `}
            </style>
        </Box>
    );
};

export default MegaMenuNavbar;
